/**
 * Android back-button handling for the Capacitor shell.
 *
 * Without this, Android's hardware/gesture back button quits the app instead of
 * navigating back — a jarring non-native behavior. We route it to in-app history
 * and only exit at the root.
 *
 * No-op on iOS / web / MobiLoud: the `backButton` event only fires in Android
 * Capacitor, and everything is guarded by isCapacitorEnvironment().
 */

import { App } from "@capacitor/app";
import Honeybadger from "@honeybadger-io/js";
import { isCapacitorEnvironment } from "./detection";

let registered = false;

function reportBackButtonError(error) {
  registered = false; // allow a later call to retry
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_back_button" },
    });
  } catch {
    // reporting must never break the app
  }
}

export function registerBackButton() {
  if (!isCapacitorEnvironment() || registered) {
    return;
  }
  registered = true; // guard re-entry; reset on failure so it can retry
  try {
    // addListener is async; surface a rejection (and allow retry) instead of
    // silently leaving Android's back button unhandled.
    const result = App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack || window.history.length > 1) {
        window.history.back();
      } else {
        // At the root — let Android background the app like a native app.
        App.exitApp();
      }
    });
    if (result && typeof result.catch === "function") {
      result.catch(reportBackButtonError);
    }
  } catch (error) {
    reportBackButtonError(error);
  }
}
